import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Particle from "../../Components/Particle.js";
import DynamicForm from "../../Components/DynamicForm";
import { END_POINT_ADMIN_ADD_USER } from "../../Config/index.js";
import { registrationForm } from "../../Constants";
import { useAuth } from "../../Context/AuthContext";

const inputClass = "w-full px-4 py-3 rounded-xl bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-blue-400 text-base";

function generatePassword(length = 8) {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

const STEPS = [
  { label: 'Account Setup', key: 'account' },
  ...registrationForm,
];

function AdminAddUser() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [account, setAccount] = useState({ emailOrMobile: '', tempPassword: generatePassword() });
  const [accountErrors, setAccountErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [createdUser, setCreatedUser] = useState(null);
  const [copied, setCopied] = useState('');
  const { token } = useAuth();
  const navigate = useNavigate();

  const isAccountStep = step === 0;
  const isLastStep = step === STEPS.length - 1;
  const currentFormStep = isAccountStep ? null : registrationForm[step - 1];

  function onChange(data) {
    setFormData(prev => ({ ...prev, ...data }));
  }

  function validateAccount() {
    const errs = {};
    const val = account.emailOrMobile.trim();
    if (!val) {
      errs.emailOrMobile = 'Email or mobile is required';
    } else if (!val.includes('@') && !/^\d{10}$/.test(val)) {
      errs.emailOrMobile = 'Enter valid email or 10-digit mobile';
    }
    if (!account.tempPassword.trim() || account.tempPassword.length < 6) {
      errs.tempPassword = 'Temporary password must be at least 6 characters';
    }
    return errs;
  }

  function handleNext() {
    if (isAccountStep) {
      const errs = validateAccount();
      if (Object.keys(errs).length > 0) { setAccountErrors(errs); return; }
      setAccountErrors({});
    }
    setStep(s => s + 1);
  }

  function handlePrevious() {
    setStep(s => s - 1);
  }

  async function handleSubmit() {
    setLoading(true);
    setApiError('');
    try {
      const val = account.emailOrMobile.trim();
      const payload = {
        ...formData,
        password: account.tempPassword,
        firstTime: true,
        level: 'user',
        ...((/^\d{10}$/.test(val)) ? { mobile: val } : { email: val }),
      };
      const response = await axios.post(END_POINT_ADMIN_ADD_USER, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreatedUser({ ...payload, ...response.data?.data });
    } catch (err) {
      setApiError(err?.response?.data?.message || 'Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function copyToClipboard(text, field) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    });
  }

  function resetForm() {
    setCreatedUser(null);
    setStep(0);
    setFormData({});
    setAccount({ emailOrMobile: '', tempPassword: generatePassword() });
    setApiError('');
  }

  if (createdUser) {
    return (
      <section>
        <div className="home-about-section w-full" id="home">
          <Particle />
          <div className="home-content">
            <div className="flex justify-center px-4">
              <div className="w-full max-w-sm">
                <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                  <div className="px-6 pt-8 pb-4 text-center">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-white mb-1">Member Added!</h2>
                    <p className="text-gray-400 text-sm">Share these credentials with the new member</p>
                  </div>
                  <div className="px-6 pb-6 flex flex-col gap-3">
                    <div className="bg-gray-700 rounded-xl p-4">
                      <p className="text-gray-400 text-xs mb-1">Username (Email / Mobile)</p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-white font-mono text-sm break-all">{account.emailOrMobile}</p>
                        <button onClick={() => copyToClipboard(account.emailOrMobile, 'username')} className="text-blue-400 text-xs shrink-0">
                          {copied === 'username' ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-700 rounded-xl p-4">
                      <p className="text-gray-400 text-xs mb-1">Temporary Password</p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-white font-mono text-sm">{account.tempPassword}</p>
                        <button onClick={() => copyToClipboard(account.tempPassword, 'password')} className="text-blue-400 text-xs shrink-0">
                          {copied === 'password' ? '✓ Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-xl p-3">
                      <p className="text-yellow-400 text-xs text-center">
                        The user will be prompted to reset their password on first login.
                      </p>
                    </div>
                    <button onClick={resetForm} className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold mt-2">
                      Add Another Member
                    </button>
                    <button onClick={() => navigate('/members')} className="w-full py-2 text-gray-400 text-sm hover:text-gray-200">
                      Back to Members
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="home-about-section w-full" id="home">
        <Particle />
        <div className="home-content">
          <div className="flex justify-center px-4">
            <div className="w-full max-w-lg">
              <div className="bg-gray-800 rounded-2xl shadow-2xl overflow-hidden">
                <div className="px-6 pt-6 pb-2">
                  <div className="flex items-center gap-3 mb-4">
                    <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <h2 className="text-xl font-bold text-white">Add New Member</h2>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {STEPS.map((_, i) => (
                      <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${i <= step ? 'bg-blue-500' : 'bg-gray-600'}`} />
                    ))}
                  </div>
                  <p className="text-gray-400 text-sm mb-4">
                    Step {step + 1} of {STEPS.length} — <span className="text-white font-medium">{STEPS[step].label}</span>
                  </p>
                </div>

                {apiError && (
                  <div className="mx-6 mb-3 px-4 py-3 bg-red-900/40 border border-red-600 rounded-xl text-red-300 text-sm">
                    {apiError}
                  </div>
                )}

                <div className="px-6 pb-6">
                  {isAccountStep ? (
                    <div className="flex flex-col gap-4">
                      <div>
                        <label className="block text-gray-300 text-sm mb-1">Email or Mobile *</label>
                        <input
                          type="text"
                          value={account.emailOrMobile}
                          onChange={e => { setAccount(a => ({ ...a, emailOrMobile: e.target.value })); setAccountErrors({}); }}
                          placeholder="Enter member email or mobile"
                          className={inputClass}
                        />
                        {accountErrors.emailOrMobile && <p className="text-red-400 text-xs mt-1">{accountErrors.emailOrMobile}</p>}
                      </div>
                      <div>
                        <label className="block text-gray-300 text-sm mb-1">Temporary Password *</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={account.tempPassword}
                            onChange={e => { setAccount(a => ({ ...a, tempPassword: e.target.value })); setAccountErrors({}); }}
                            placeholder="Temporary password"
                            className={inputClass}
                          />
                          <button
                            type="button"
                            onClick={() => setAccount(a => ({ ...a, tempPassword: generatePassword() }))}
                            className="px-3 py-2 rounded-xl bg-gray-600 text-gray-300 text-sm whitespace-nowrap hover:bg-gray-500"
                          >
                            Generate
                          </button>
                        </div>
                        {accountErrors.tempPassword && <p className="text-red-400 text-xs mt-1">{accountErrors.tempPassword}</p>}
                        <p className="text-gray-500 text-xs mt-1">User must reset this on first login</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-white font-semibold mb-4">{currentFormStep.label}</h3>
                      {currentFormStep.config.map((item) => (
                        <DynamicForm
                          key={item.fieldName}
                          formConfig={{ ...item, onChange, formName: currentFormStep.formName }}
                          isShowUpload={true}
                          setShowUpload={() => {}}
                          formData={formData}
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3 mt-6">
                    {step > 0 && (
                      <button
                        type="button"
                        onClick={handlePrevious}
                        className="flex-1 py-3 rounded-xl bg-gray-700 text-gray-300 font-medium hover:bg-gray-600"
                      >
                        Back
                      </button>
                    )}
                    {isLastStep ? (
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60"
                      >
                        {loading ? 'Creating…' : 'Create Member'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                      >
                        Next
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AdminAddUser;
